import React from 'react';
import { imageUrl } from '../../utils';

interface BannerHeaderProps {
  pillLabel: string;
  title: string;
  description: string;
  image: string;
  images?: string[]; // Optional array of images for row layout
  imageAlt?: string;
  primaryButton?: {
    label: string;
    onClick?: () => void;
  };
  secondaryButton?: {
    label: string;
    onClick?: () => void;
  };
}

const BannerHeader = ({
  pillLabel,
  title,
  description,
  image,
  images,
  imageAlt = 'Banner image',
  primaryButton,
  secondaryButton,
}: BannerHeaderProps) => {
  return (
    <section className="relative bg-gradient-to-r from-[#FAC72E] to-[#FA832E] overflow-hidden">
      <div className="container relative mx-auto px-4 py-20 sm:py-32">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <div className="space-y-6">
            {/* Pill/Badge */}
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ffffff] text-[#1a1a1a] text-sm font-semibold shadow-lg hover:shadow-xl transition-shadow"
              style={{ fontFamily: 'var(--fontPrimary)' }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4"
              >
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path>
                <path d="M20 3v4"></path>
                <path d="M22 5h-4"></path>
                <path d="M4 17v2"></path>
                <path d="M5 18H3"></path>
              </svg>
              {pillLabel}
            </div>

            {/* Title */}
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#263238] leading-tight"
              style={{ fontFamily: 'var(--fontBase)' }}
            >
              {title}
            </h1>

            {/* Description */}
            <p
              className="text-xl text-[#263238] leading-relaxed"
              style={{ fontFamily: 'var(--fontPrimary)' }}
            >
              {description}
            </p>

            {/* Buttons */}
            {(primaryButton || secondaryButton) && (
              <div className="flex flex-wrap gap-4">
                {primaryButton && (
                  <button
                    onClick={primaryButton.onClick}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium bg-[#263238] text-white hover:bg-[#263238]/90 h-11 px-8 rounded-full shadow-md"
                  >
                    {primaryButton.label}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 ml-2"
                    >
                      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path>
                      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path>
                      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"></path>
                      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"></path>
                    </svg>
                  </button>
                )}
                {secondaryButton && (
                  <button
                    onClick={secondaryButton.onClick}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium bg-[#ffffff] text-[#1a1a1a] hover:bg-[#ffffff]/90 h-11 px-8 rounded-full border-2 border-transparent"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path>
                      <path d="M2 12h20"></path>
                    </svg>
                    {secondaryButton.label}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Right side - Image(s) */}
          <div className="relative group">
            {images && images.length > 0 ? (
              // Multiple images in a row
              <div className="flex gap-4 items-center justify-center">
                {images.map((img, index) => (
                  <div key={index} className="relative">
                    <div className="absolute -inset-2 bg-gradient-to-r from-[#FA832E] to-[#F5CA09] rounded-3xl opacity-20 blur-xl group-hover:opacity-30 transition-opacity"></div>
                    <div className="relative rounded-3xl overflow-hidden">
                      <img
                        src={imageUrl(img)}
                        alt={`${imageAlt} ${index + 1}`}
                        className="w-full h-full max-w-xs max-h-[500px] object-contain object-center rounded-3xl shadow-2xl transform group-hover:scale-[1.02] transition-transform duration-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Single image
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-[#FA832E] to-[#F5CA09] rounded-3xl opacity-20 blur-xl group-hover:opacity-30 transition-opacity"></div>
                <div className="relative w-full rounded-3xl overflow-hidden">
                  <img
                    src={imageUrl(image)}
                    alt={imageAlt}
                    className="w-full h-full object-contain object-center rounded-3xl shadow-2xl transform group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BannerHeader;

